import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateUserMutation } from "slices/usersApi";
import Swal from "sweetalert2";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";
import AddIcon from "@mui/icons-material/Add";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

function ProfileEdit({ open, data, usertype, onClose }) {
  console.log("data", data);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (data?.image) {
      setPreview(IMAGE_URL + data.image);
    } else {
      setPreview(""); // or a placeholder image if you want
    }
  }, [data]);

  console.log("data", data, preview);
  const [update] = useUpdateUserMutation();

  const formik = useFormik({
    enableReinitialize: true, // important for pre-filling on prop change
    initialValues: {
      UserId: data?._id,
      image: data?.image || "",
      name: data?.name || "",
      email: data?.email || "",
      phone: data?.mobile || "",
      perMinutCharges: data?.perMinutCharges || "",
      status: data?.status || "active", // default to "active"
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone is required"),
      perMinutCharges: Yup.number()
        .typeError("Charges must be a number")
        .positive("Charges must be positive")
        .when([], {
          is: () => usertype !== "user", // only required if not user
          then: (schema) => schema.required("Per minute charges are required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      status: Yup.string()
        .oneOf(["active", "inactive"], "Invalid status")
        .required("Status is required"),
    }),

    onSubmit: async (values) => {
      try {
        console.log("Updated values:", values);

        const response = await update({ id: data._id, body: values }).unwrap();
        if (response) {
          onClose();
        }
      } catch (error) {
        onClose();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.data?.message || error?.message || "Server error",
        });
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" mb={2}>
          Update Profile
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          {/* Profile Image Upload */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            {/* <InputLabel className="mb-2">Image</InputLabel> */}
            <Box
              sx={{
                width: 150,
                height: 150,
                border: "2px dashed #ccc",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: "pointer",
                overflow: "hidden",
                backgroundColor: "#f9f9f9",
              }}
              onClick={() =>
                document.getElementById("profile-image-input").click()
              }
            >
              {preview ? (
                <img
                  crossOrigin="anonymous"
                  src={preview}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <AddIcon sx={{ fontSize: 40, color: "#aaa" }} />
              )}

              <input
                type="file"
                id="profile-image-input"
                hidden
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const uploadedPath = await uploadImage(file);
                    formik.setFieldValue("image", uploadedPath); // keep it in form values

                    setPreview(IMAGE_URL + uploadedPath);
                  }
                }}
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            margin="normal"
            name="name"
            label="Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />

          <TextField
            fullWidth
            margin="normal"
            name="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            fullWidth
            type="number"
            margin="normal"
            name="phone"
            label="Phone"
            value={formik.values.phone}
            InputProps={{
              readOnly: true,
            }}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />

          <TextField
            fullWidth
            margin="normal"
            name="status"
            label="Account Status"
            select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.status && Boolean(formik.errors.status)}
            helperText={formik.touched.status && formik.errors.status}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>

          {usertype !== "user" && (
            <TextField
              fullWidth
              margin="normal"
              name="perMinutCharges"
              label="Per Minute Charges (₹)"
              value={formik.values.perMinutCharges}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.perMinutCharges &&
                Boolean(formik.errors.perMinutCharges)
              }
              helperText={
                formik.touched.perMinutCharges && formik.errors.perMinutCharges
              }
            />
          )}

          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Update
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default ProfileEdit;
